import { useState } from "react";
import { PageHeader } from "../components/pages/PageHeader";
import { AssignedAuditsPanel } from "../components/dashboard/AssignedAuditsPanel";
import { Card } from "../components/ui/Card";
import { icons } from "../components/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/useAuth";
import type { DashboardAssignedAudit } from "../services/labService";
import { type OrganisationDetailsResponse } from "../services/organisationService";
import { AdminTrainingVideoModal } from "../components/AdminTrainingVideoModal";
import { AdminsList } from "../components/AdminsList";
import { PlayCircle } from "lucide-react";

interface AdminDashboardPageV2Props {
  assignedAudits?: DashboardAssignedAudit[];
  labTypeSubscriptions?: string[];
  organisationName: string;
  organisation?: OrganisationDetailsResponse;
}

export const AdminDashboardPage = ({
  assignedAudits = [],
  labTypeSubscriptions,
  organisationName,
  organisation
}: AdminDashboardPageV2Props) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showVideoModal, setShowVideoModal] = useState(false);

  return (
    <div className="l0-layout space-y-6">
        {/* Header */}
        <PageHeader
          pageTitle={"Admin Dashboard"}
          labTypeSubscriptions={labTypeSubscriptions}
          organisationName={organisationName}
          adminGuidePath="/admin-guide"
        />

        <AssignedAuditsPanel assignedAudits={assignedAudits} />

        <Card variant="primary">
          <div className="card-body">
            <div>
              <ul>
                <li>
                  Welcome to your admin dashboard! Choose an option below to set
                  up and manage your organisation.
                </li>
                <li>
                  Enter your variables, manage labs, and view impact reports to
                  get the most out of the platform.
                </li>
              </ul>
            </div>
            <div className="actions grid grid-cols-2 grid-rows-2 gap-4">
              <Card variant="secondary" >
                <div className="card-body">
                  <div className={"flex flex-row gap-4 items-center"}>
                    <div className="w-[18px]">
                      <img src={icons.admin_input} />
                    </div>
                    <h3 className="card-title">Enter Variables</h3>
                  </div>
                  <p>Add your specific utility & waste costs etc.</p>
                  <div className="card-actions mt-4">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        navigate("variables");
                      }}
                    >
                      Start
                    </button>
                  </div>
                </div>
              </Card>
              <Card variant="secondary" >
                <div className="card-body">
                  <div className={"flex flex-row gap-4 items-center"}>
                    <div className="w-[18px]">
                      <img src={icons.admin_flask_add} />
                    </div>
                    <h3 className="card-title">Labs</h3>
                  </div>
                  <p>Create labs, manage membership, and track certification readiness.</p>
                  {organisation && (
                    <p className="text-sm text-base-content/70 mt-2">
                      {organisation.total_labs || 0} / {organisation.lab_limit || 0} labs created
                    </p>
                  )}
                  <div className="card-actions mt-4">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        navigate("labs");
                      }}
                    >
                      Open
                    </button>
                  </div>
                </div>
              </Card>
              <Card variant="secondary" >
                <div className="card-body">
                  <div className={"flex flex-row gap-4 items-center"}>
                    <div className="w-[18px]">
                      <img src={icons.admin_user_analyticcs} />
                    </div>
                    <h3 className="card-title">Impact Reports</h3>
                  </div>
                  <p>Check your organisation’s estimated outcomes.</p>
                  <div className="card-actions mt-4">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        navigate("impact-reports");
                      }}
                    >
                      View
                    </button>
                  </div>
                </div>
              </Card>
                <Card variant="secondary" >
                  <div className="card-body">
                    <div className={"flex flex-row gap-4 items-center"}>
                      <div className="w-[18px]">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="card-title">Manage Invitations</h3>
                    </div>
                    <p>Send invitations to new users to join your organisation</p>
                    <div className="card-actions mt-4">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          navigate("/invitations");
                        }}
                      >
                        Manage
                      </button>
                    </div>
                  </div>
                </Card>
              {user?.is_organisation_admin && (
                <Card variant="secondary" className="border-emerald-200">
                  <div className="card-body">
                    <div className={"flex flex-row gap-4 items-center"}>
                      <div className="w-[18px]">
                        <PlayCircle className="text-emerald-600" />
                      </div>
                      <h3 className="card-title">Admin Training</h3>
                    </div>
                    <p>Watch the platform induction for Administrators.</p>
                    <div className="card-actions mt-4">
                      <button
                        className="btn btn-primary btn-sm gap-2"
                        onClick={() => setShowVideoModal(true)}
                      >
                        <PlayCircle size={16} />
                        Watch Video
                      </button>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </Card>

        {/* Organisation Admins Section */}
        <Card variant="primary">
          <div className="card-body">
            <div className="flex justify-between items-center mb-4">
              <h2 className="card-title">Organisation Admins</h2>
              <div className="badge badge-outline">
                {organisation?.admins?.length || 0} admin{organisation?.admins?.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* Description for transparency requirement */}
            <div className="mb-4">
              <p className="text-sm text-base-content/70">
                View all individuals with administrative access to your organization. 
                This helps improve transparency and clarity around permission management.
              </p>
            </div>

            {/* Admins List Component */}
            <AdminsList 
              admins={organisation?.admins || []} 
              loading={!organisation} 
            />
          </div>
        </Card>

        <AdminTrainingVideoModal
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
        />
    </div>
  );
};
